import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import ConfigurationPanel from './VerificationConfigPanel';
import FileUploadSection from './FileUploadSection';
import ResultsDisplay from './ResultsDisplay';
import { downloadBlob } from '../../utils';
import { AuthoritativeVerificationMode } from '../../models';
import useComparisonProfiles from '../../hooks/useComparisonProfiles';

const PublicationVerifier = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [email, setEmail] = useState('abbas.rahimi@jku.at');
  const [apiKey, setApiKey] = useState('');
  const [enrichDoi, setEnrichDoi] = useState(true);
  const [authoritativeVerificationMode, setAuthoritativeVerificationMode] = useState(
    AuthoritativeVerificationMode.CASCADE
  );
  /** When null, omit existence_check_mode (API falls back to authoritative_verification_mode). */
  const [existenceCheckMode, setExistenceCheckMode] = useState(null);
  const [executionName, setExecutionName] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResults, setVerificationResults] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [useStorage, setUseStorage] = useState(false);
  const {
    profiles: verificationProfiles,
    loading: profilesLoading,
    defaultProfileId,
  } = useComparisonProfiles('verification');
  const [verificationProfileId, setVerificationProfileId] = useState(null);

  useEffect(() => {
    if (defaultProfileId != null && verificationProfileId == null) {
      setVerificationProfileId(defaultProfileId);
    }
  }, [defaultProfileId, verificationProfileId]);

  const startVerification = async () => {
    if (!selectedFile || isVerifying) {
      return;
    }

    if (useStorage && !executionName.trim()) {
      setError('Please provide an execution name when using storage mode.');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      // Critical: backend expects enrich_doi as multipart form field.
      // Send as string for FormData compatibility with FastAPI Form(bool).
      formData.append('enrich_doi', enrichDoi ? 'true' : 'false');
      // New OpenAPI: authoritiative DB verification strategy (cascade|multi)
      formData.append('authoritative_verification_mode', authoritativeVerificationMode);
      if (existenceCheckMode != null && String(existenceCheckMode).trim() !== '') {
        formData.append('existence_check_mode', String(existenceCheckMode).trim());
      }

      const emailTrimmed = (email || '').trim();
      const apiKeyTrimmed = (apiKey || '').trim();
      if (emailTrimmed) formData.append('email', emailTrimmed);
      if (apiKeyTrimmed) formData.append('api_key', apiKeyTrimmed);
      
      if (useStorage) {
        formData.append('execution_name', executionName.trim());
      }
      if (verificationProfileId != null) {
        // OpenAPI: verifier endpoints accept comparison_profile_id (purpose=verification)
        formData.append('comparison_profile_id', String(verificationProfileId));
      }

      const results = useStorage 
        ? await apiService.verifyPublicationsWithStorage(formData)
        : await apiService.verifyPublications(formData);
      
      setVerificationResults(results);
    } catch (error) {
      setError('Error during verification: ' + error.message);
    } finally {
      setIsVerifying(false);
    }
  };

  const exportResults = async () => {
    if (!verificationResults) {
      alert('No results to export.');
      return;
    }
    try {
      const blob = await apiService.exportVerificationResults(verificationResults, 'text');
      downloadBlob(blob, `publication_verification_${new Date().toISOString().split('T')[0]}.txt`);
    } catch (error) {
      setError('Error exporting results: ' + error.message);
    }
  };

  const clearResults = () => {
    setSelectedFile(null);
    setVerificationResults(null);
    setError(null);
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          <h1 className="mb-4">
            <i className="fas fa-search"></i> Publication Verifier
          </h1>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Configuration Section */}
      <ConfigurationPanel
        email={email}
        setEmail={setEmail}
        apiKey={apiKey}
        setApiKey={setApiKey}
        enrichDoi={enrichDoi}
        setEnrichDoi={setEnrichDoi}
        authoritativeVerificationMode={authoritativeVerificationMode}
        setAuthoritativeVerificationMode={setAuthoritativeVerificationMode}
        existenceCheckMode={existenceCheckMode}
        setExistenceCheckMode={setExistenceCheckMode}
        useStorage={useStorage}
        setUseStorage={setUseStorage}
        executionName={executionName}
        setExecutionName={setExecutionName}
        verificationProfiles={verificationProfiles}
        verificationProfileId={verificationProfileId}
        setVerificationProfileId={setVerificationProfileId}
        profilesLoading={profilesLoading}
      />

      {/* File Upload Section */}
      <FileUploadSection
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        dragOver={dragOver}
        setDragOver={setDragOver}
        onStartVerification={startVerification}
        onExportResults={exportResults}
        onClearResults={clearResults}
        error={error}
        setError={setError}
        isVerifying={isVerifying}
        verificationResults={verificationResults}
      />

      {/* Progress Section */}
      {isVerifying && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="flex-grow-1">
                    <div className="progress">
                      <div 
                        className="progress-bar progress-bar-striped progress-bar-animated" 
                        role="progressbar" 
                        style={{width: '100%'}}
                      ></div>
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-center">Searching publications across databases...</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {verificationResults && (
        <ResultsDisplay
          verificationResults={verificationResults}
          email={email}
          apiKey={apiKey}
        />
      )}
    </div>
  );
};

export default PublicationVerifier;